<script setup lang="ts">
import { Person } from "../data/types";
import { DELETE, GET, POST } from "../data/api.ts";
import { onMounted, ref } from "vue";
import ToolTip from "../components/ToolTip.vue";
import { useToastStore } from "../data/store.ts";

const toaster = useToastStore();

const people = ref<Person[]>([]);
const editing = ref<string | null>(null); // id of person in edit focus
const delConfirm = ref<string | null>(null); // id of person to delete

onMounted(() => {
  fetchPeople();
});

//encodeURIComponent(params[param])
function fetchPeople() {
  GET("/people/")
    .then((response) => {
      people.value = response as Person[];
    })
    .catch(() => {
      toaster.toast("Failed to load people", "FAIL");
    });
}

function deletePerson(uid: string) {
  delConfirm.value = null;
  const index = people.value.map((person) => person.id).indexOf(uid);
  DELETE("/people/", { uid })
    .then(() => {
      people.value?.splice(index, 1);
      toaster.toast("Deleted!", "SUCC");
    })
    .catch(() => {
      toaster.toast("Failed to delete", "FAIL");
    });
}

function savePerson(person: Person) {
  POST("/people/", person)
    .then(() => {
      toaster.toast("Saved!", "SUCC");
      editing.value = null;
    })
    .catch(() => {
      toaster.toast("Failed to save!", "FAIL");
    });
}

function addPerson() {
  const newPerson: Person = {
    id: crypto.randomUUID(),
    name: "",
    short: "",
    acro: "",
    group: "",
    // @ts-expect-error
    type: null,
  };
  people.value.push(newPerson);
  editing.value = newPerson.id;
}

function personValid(person: Person) {
  return (
    nameValid(person) &&
    shortValid(person) &&
    acroValid(person) &&
    groupValid(person) &&
    typValid(person)
  );
}

function nameValid(person: Person) {
  return person.name.trim().length > 3 && person.name.trim().length < 30;
}

function shortValid(person: Person) {
  return person.short.trim().length >= 3 && person.short.trim().length < 12;
}

function acroValid(person: Person) {
  return person.acro.trim().length === 2;
}

function groupValid(person: Person) {
  if (person.group) {
    return person.group.trim().length >= 2 && person.group.trim().length < 15;
  }
  return true;
}

function typValid(person: Person) {
  return !!person.type;
}

function copyLink(person: Person) {
  navigator.clipboard.writeText(
    `${window.location.origin}${import.meta.env.TBS_FRONTEND_BASE ?? ""}/#/desks/${person.id}`,
  );
}
</script>

<template>
  <h1 class="mt-5">Add & edit people</h1>

  <div class="col col-lg-8 m-auto mb-5 text-start">
    <template v-for="person in people" :key="person.id">
      <template v-if="editing === person.id">
        <div class="row mb-2 border-top pt-3">
          <div class="col text-start">
            ID: <code>{{ person.id }}</code>
          </div>
          <div class="col ms-auto text-end">
            <span v-if="delConfirm">
              Really?
              <button
                class="btn btn-sm btn-danger me-2"
                style="font-size: 0.7rem"
                @click="deletePerson(person.id)"
              >
                Yes
              </button>
              <button
                class="btn btn-sm btn-success me-2"
                style="font-size: 0.7rem"
                @click="delConfirm = null"
              >
                No
              </button>
            </span>

            <button
              class="btn btn-sm btn-outline-danger me-2"
              style="font-size: 0.7rem"
              @click="delConfirm = person.id"
            >
              <i class="bi-trash" /> Delete
            </button>
            <button
              class="btn btn-sm btn-outline-success me-2"
              style="font-size: 0.7rem"
              :disabled="!personValid(person)"
              @click="savePerson(person)"
            >
              <i class="bi-floppy" /> Save
            </button>

            <i class="bi-x-octagon text-muted" @click="editing = null" />
          </div>
        </div>
        <div class="border-bottom mb-2 pb-3 row g-2">
          <div class="col-md-4 col-7">
            <input
              v-model="person.name"
              class="form-control form-control-sm"
              type="text"
              placeholder="Full name"
              :class="{ 'is-invalid': !nameValid(person) }"
            />
          </div>
          <div class="col-md-2 col-5">
            <input
              v-model="person.short"
              class="form-control form-control-sm"
              type="text"
              placeholder="Short name"
              :class="{ 'is-invalid': !shortValid(person) }"
            />
          </div>
          <div class="col-md-1 col-3">
            <input
              v-model="person.acro"
              class="form-control form-control-sm"
              type="text"
              placeholder="Acronym"
              :class="{ 'is-invalid': !acroValid(person) }"
            />
          </div>
          <div class="col-md-3 col-5">
            <input
              v-model="person.group"
              class="form-control form-control-sm"
              type="text"
              placeholder="Group"
              :class="{ 'is-invalid': !groupValid(person) }"
            />
          </div>
          <div class="col-md-2 col-4">
            <select
              v-model="person.type"
              class="form-select form-select-sm"
              :class="{ 'is-invalid': !typValid(person) }"
            >
              <option :value="null" selected>Type</option>
              <option value="FULL">Full-time</option>
              <option value="STUD">Student</option>
              <option value="GUEST">Guest</option>
            </select>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="row text-start p-1 prow">
          <div class="col-1 fw-bold">
            {{ person.acro }}
          </div>
          <!--          <div class="col-1 fst-italic">-->
          <!--            {{ person.short }}-->
          <!--          </div>-->
          <div class="col-4">
            {{ person.name }}
          </div>
          <div class="col-2 text-muted">
            {{ person.group }}
          </div>
          <!--          <div class="col-1">-->
          <!--            {{ person.type }}-->
          <!--          </div>-->
          <div class="col-4 small">
            <code>{{ person.id }}</code>
          </div>
          <div class="col ms-auto text-end">
            <ToolTip class="me-2" text="Copy login link">
              <i class="bi-copy" role="button" @click="copyLink(person)" />
            </ToolTip>
            <i class="bi-pen" role="button" @click="editing = person.id" />
          </div>
        </div>
      </template>
    </template>

    <div class="w-100 text-end mt-4">
      <button class="btn btn-sm btn-outline-secondary" @click="addPerson">
        <i class="bi-person-add" /> Add someone
      </button>
    </div>
  </div>
</template>

<style scoped>
.prow:hover {
  background-color: #f5f5f5 !important;
}
</style>
